import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/isSpoofed'
import { checkoutAccommodationOfferView } from '../../view/accommodation'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { RESERVE_FOR_ZERO_PAYMENT_SCHEDULE_TEMPLATE_ID } from 'constants/payment'
import config from 'constants/config'
import { isAccommodationItem, isBedbankItem, isInstantBookingLEHotelItem, isLEHotelItem, isNoProtectionInsuranceItem, findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getAllItemsDiscountTotals from '../getAllItemsDiscountTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { getInsuranceTotals } from '../insurance'
import { getAccommodationBreakdownView } from 'checkout/selectors/view/accommodation'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import { getPaymentScheduleKey } from 'lib/payment/paymentScheduleUtils'
import isPaymentScheduleEnabled from './isPaymentScheduleEnabled'
import { buildPaymentScheduleItems, calculateGiftCardAmount } from './paymentScheduleItemUtils'
import getServiceFeeDestination from 'checkout/lib/payment/getServiceFeeDestination'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { isLppHotelOffer, isLEHotel } from 'lib/offer/offerTypes'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'

type ItemView = App.Checkout.AccommodationItemView

function isValidReserveForZeroOrder(cartItems: Array<App.Checkout.AnyItem>): boolean {
  const reservableItems = cartItems.filter(item => !isNoProtectionInsuranceItem(item))
  // false if there are no reservable items, or if any of the items are not LE hotel items (e.g. activities, packages with non-hotel components)
  if (!reservableItems.length || !reservableItems.every(isLEHotelItem)) {
    return false
  }

  const offerIds = new Set(reservableItems.map(item => item.offerId))
  return offerIds.size === 1
}

function isBedbankRateEligibleForReserveForZero(
  item: App.Checkout.BedbankHotelItem,
  cartItems: Array<App.Checkout.AnyItem>,
  offers: Record<string, App.AnyOffer>,
  bedbankOffers: Record<string, App.BedbankOffer>,
  bedbankRatesByOffer: App.State['offer']['bedbankOfferRates'],
): boolean {
  const checkoutOffer = offers[item.offerId]
  const offer = isLppHotelOffer(checkoutOffer) ? checkoutOffer : bedbankOffers[item.offerId]
  const sessionItems = cartItems.filter(isBedbankItem).filter(cartItem =>
    cartItem.offerId === item.offerId &&
    cartItem.checkIn === item.checkIn &&
    cartItem.checkOut === item.checkOut &&
    (cartItem.sessionId ?? 'none') === (item.sessionId ?? 'none'),
  )
  const combinedRateKey = getBedbankRateKey(sessionItems.map(sessionItem => sessionItem.occupancy), item.checkIn, item.checkOut)
  const perRoomRateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
  const rates = bedbankRatesByOffer[item.offerId]?.[combinedRateKey]
    ?? bedbankRatesByOffer[item.offerId]?.[perRoomRateKey]
  const matchedRate = rates?.find(rate =>
    rate.id === item.roomRateId &&
    rate.roomId === item.roomId &&
    rate.isFlightBundle === item.isFlightBundle,
  )
  return isLppHotelOffer(offer) && matchedRate?.isReserveForZeroEligible === true
}

function isValidBedbankReserveForZeroOrder(
  cartItems: Array<App.Checkout.AnyItem>,
  offers: Record<string, App.AnyOffer>,
  bedbankOffers: Record<string, App.BedbankOffer>,
  bedbankRatesByOffer: App.State['offer']['bedbankOfferRates'],
): boolean {
  const reservableItems = cartItems.filter(item => !isNoProtectionInsuranceItem(item))
  if (!reservableItems.length || !reservableItems.every(isAccommodationItem) || !reservableItems.every(isBedbankItem)) {
    return false
  }

  const offerIds = new Set(reservableItems.map(item => item.offerId))

  return offerIds.size === 1 &&
    reservableItems.every(item => isBedbankRateEligibleForReserveForZero(item, cartItems, offers, bedbankOffers, bedbankRatesByOffer))
}

export const isReserveForZeroPaymentScheduleFlagOn = createSelector(
  (state: App.State) => isSpoofed(state),
  (_state: App.State) => config.RESERVE_FOR_ZERO_PAYMENT_SCHEDULE_ENABLED,
  (isSpoofed, flagEnabled) => isSpoofed && flagEnabled,
)

export const isReserveForZeroPaymentScheduleAvailable = createSelector(
  isReserveForZeroPaymentScheduleFlagOn,
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (
    isFlagOn,
    paymentScheduleEnabled,
    postPurchase,
    isGift,
    accommodationOfferView,
    cartItems,
    offers,
    bedbankOffers,
    bedbankRatesByOffer,
    offerAvailableRatesByOccupancy,
  ) => {
    if (!isFlagOn || !paymentScheduleEnabled || postPurchase || isGift) {
      return false
    }
    const isExistingLEEligible = accommodationOfferView.data.every(view => {
      if (!('offer' in view) || !isLEHotel(view.offer)) {
        return false
      }
      const offer = view.offer
      const checkIn = view.startDate ? moment(view.startDate) : undefined
      return view.itemViews.length > 0 && view.itemViews.every((itemView: ItemView) => {
        const pkg = 'pkg' in itemView ? itemView.pkg : undefined
        return isPackageEligibleForZeroReservation(
          offer,
          checkIn,
          pkg?.roomRate?.cancellationPolicy?.type,
          'pkg' in itemView && isInstantBookingLEHotelItem(itemView.item) ? offerAvailableRatesByOccupancy[offer.id]?.[
            buildAvailableRateKey(itemView.item.checkIn, itemView.item.checkOut, [itemView.item.occupancy])
          ]?.rates.find(rate => rate.packageUniqueKey === pkg?.uniqueKey)?.cancellationPolicies : undefined,
          pkg?.ignoreDynamicCancellationPolicy,
        )
      })
    }) && isValidReserveForZeroOrder(cartItems)

    const isBedbankEligible = isValidBedbankReserveForZeroOrder(
      cartItems,
      offers,
      bedbankOffers,
      bedbankRatesByOffer,
    )
    return isExistingLEEligible || isBedbankEligible
  },
)

export const getReserveForZeroPaymentScheduleRequestInfo = createSelector(
  isReserveForZeroPaymentScheduleAvailable,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => state.checkout.payment.useGiftCard,
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => checkoutAccommodationOfferView(state),
  (
    isAvailable,
    cartItems,
    offers,
    bedbankOffers,
    itemViews,
    discountTotalToCartItemsMap,
    checkoutWithMemberPrice,
    insuranceTotals,
    accommodationBreakdownView,
    currency,
    isCreditApplied,
    isGiftCardApplied,
    redeemedGiftCards,
    accommodationOfferView,
  ): App.Checkout.PaymentScheduleRequestInfo | null => {
    if (!isAvailable) {
      return null
    }

    const accommodationItem = cartItems.find(isLEHotelItem) ?? cartItems.find(isBedbankItem)
    if (!accommodationItem) {
      return null
    }

    const startDate = isBedbankItem(accommodationItem)
      ? accommodationItem.checkIn
      : accommodationOfferView.data[0]?.startDate
    if (!startDate) {
      return null
    }

    const giftCardAmount = calculateGiftCardAmount(redeemedGiftCards)
    const checkoutOffer = offers[accommodationItem.offerId]
    const offer = isBedbankItem(accommodationItem)
      ? (isLppHotelOffer(checkoutOffer) ? checkoutOffer : bedbankOffers[accommodationItem.offerId])
      : checkoutOffer
    const serviceFeeDestination = getServiceFeeDestination(accommodationItem, offer)

    const items = buildPaymentScheduleItems(cartItems, {
      offers,
      discountTotalToCartItemsMap,
      checkoutWithMemberPrice,
      insuranceTotals,
      accommodationBreakdownView,
      itemViews,
    })

    if (!items) {
      return null
    }

    return {
      templateId: RESERVE_FOR_ZERO_PAYMENT_SCHEDULE_TEMPLATE_ID,
      bookingInfo: {
        bookingDate: moment().format(ISO_DATE_FORMAT),
        checkinDate: startDate,
        supplierDueDate: startDate,
        isCreditApplied,
        isGiftCardApplied,
        giftCardAmount,
        isPaymentScheduleRebooking: false,
        serviceFeeDestination,
        currency,
        items,
      },
    }
  },
)

export const getReserveForZeroPaymentSchedule = createSelector(
  (state: App.State) => state.paymentSchedule,
  getReserveForZeroPaymentScheduleRequestInfo,
  (paymentSchedule, reserveForZeroRequestInfo): App.PaymentScheduleChargeData | undefined => {
    const key = getPaymentScheduleKey(reserveForZeroRequestInfo)

    if (key && paymentSchedule.charges && key in paymentSchedule.charges) {
      return paymentSchedule.charges[key]
    }

    return undefined
  },
)

export const isReserveForZeroPaymentScheduleEligible = createSelector(
  getReserveForZeroPaymentSchedule,
  (reserveForZeroSchedule): boolean => reserveForZeroSchedule?.eligible ?? false,
)
